import { call, delay, put, select, take, takeLatest } from 'redux-saga/effects';
import furnitureData from '../../../lib/modelBuilder/tests/fixtures/furniture.json';

import ActionTypes, {
  BASE_CABINET,
  CABINET_URL,
  FURNITURE_URL,
} from 'containers/Configurator/constants/furniture';
import { requestGet } from 'utils/requests';
import {
  loadFurnitureFailureAction,
  loadFurnitureSuccessAction,
  setImageAction,
  updateCabinetSuccess,
} from 'containers/Configurator/actions/furniture';
import { CabinetJson, FurnitureJson } from 'lib/modelBuilder/Data';
import { makeSelectPieces } from 'containers/Configurator/selectors/pieces';
import { Cabinet } from 'lib/modelBuilder/entities/Cabinet';
import { makeSelectColors } from 'containers/Configurator/selectors/colors';
import { downloadPiecesFromAPI } from 'containers/Configurator/sagas/pieces';
import { downloadColorsFromAPI } from 'containers/Configurator/sagas/colors';
import { makeSelectRules } from 'containers/Configurator/selectors/rules';
import { downloadRulesFromAPI } from 'containers/Configurator/sagas/rules';
import { Furniture } from 'lib/modelBuilder/entities/Furniture';
import {
  makeSelectFurniture,
  makeSelectSelectedIdCabinet,
} from 'containers/Configurator/selectors/furniture';
import { switchTabAction } from 'containers/Header/actions';
import TabNames from 'containers/Header/TabNames';
import {
  makeSelectActiveTab,
  makeSelectDisplayedTab,
} from 'containers/Header/selectors';
import { getPNGimage } from 'containers/Viewer';
import { loadFurniture } from 'helpers/tools';
import { toast } from 'react-toastify';
import { colorsDataXML } from '../../../lib/modelBuilder/tests/fixtures/old/colors.data';
import {
  colorParser,
  dataParser,
  loadOldCabinet,
} from '../../../lib/modelBuilder/utils/conversionUtils/converter';
import i18n from '../../../i18n';
import properties, { FurnitureCallbackInfos } from 'helpers/config';

function* loadingCabinetSaga(action: any) {
  let colors = yield select(makeSelectColors());
  if (colors.length === 0) colors = yield call(downloadColorsFromAPI);

  let pieces = yield select(makeSelectPieces());
  if (!pieces) pieces = yield call(downloadPiecesFromAPI);

  let rules = yield select(makeSelectRules());
  if (!rules) rules = yield call(downloadRulesFromAPI);

  let { reference } = action.payload;
  const { old } = action.payload;

  if (!reference) {
    reference = properties.product;
  }

  if (!reference) {
    // TODO: Store initial furniture in local storage and compare with properties
    //  if no match, refresh, then use the local storage furniture, we can also propose the choice with modal

    const localFurniture = loadFurniture();
    if (localFurniture) {
      yield put(loadFurnitureSuccessAction(localFurniture));
      return;
    } else {
      reference = BASE_CABINET;
    }
  } else {
    localStorage.removeItem('furniture');
  }

  if (old && reference) {
    const colorsData = colorParser(colorsDataXML);
    const dataXML = yield call(loadOldCabinet, reference);
    const cabinet = dataParser(dataXML, colorsData);
    const oldFurniture = new Furniture();
    oldFurniture.add(cabinet);
    yield put(loadFurnitureSuccessAction(oldFurniture));
    return;
  }

  let url = `${CABINET_URL}/${reference}/`;
  let wholeFurniture = false;

  if (reference.charAt(0) === 'f') {
    // The reference of furniture always start with 'f'
    const id = reference.substring(1);
    url = `${FURNITURE_URL}/${id}/`;
    wholeFurniture = true;
  }

  if (process.env.NODE_ENV !== 'production') {
    try {
      yield call(requestGet, url);
    } catch (e) {
      if (e.statusText.includes('Cannot connect.')) {
        // @ts-ignore
        const furniture = Furniture.fromModel(furnitureData as FurnitureJson);
        if (furniture) {
          yield put(loadFurnitureSuccessAction(furniture));
        }
        return;
      }
    }
  }

  if (!pieces || !colors) {
    yield put(
      loadFurnitureFailureAction({
        message: 'Error when loading pieces or colors',
      }),
    );
    return;
  }
  // const cabinet = Cabinet.fromModel(cabinetsData as CabinetJson);
  // const newFurniture = new Furniture();
  // newFurniture.add(cabinet);
  // yield put(loadFurnitureSuccessAction(newFurniture));
  // continue;

  try {
    const response = yield call(requestGet, url);
    if (wholeFurniture) {
      const furniture = Furniture.fromModel(response as FurnitureJson);
      if (furniture) {
        yield put(loadFurnitureSuccessAction(furniture as Furniture));
      }
    } else {
      const cabinet = Cabinet.fromModel(response as CabinetJson);
      if (cabinet) {
        const simpleFurniture = new Furniture();
        simpleFurniture.add(cabinet as Cabinet);
        yield put(loadFurnitureSuccessAction(simpleFurniture));
      }
    }
  } catch (error) {
    yield put(loadFurnitureFailureAction(error));
    toast.error(`Could not load the furniture; Error ${error.statusText}`);

    // @ts-ignore
    const furniture = Furniture.fromModel(furnitureData as FurnitureJson);
    if (furniture) {
      yield put(loadFurnitureSuccessAction(furniture));
    }
    // throw new Error(`Error when loading the furniture ${error}`);
  }
}

export default function* furnitureSaga() {
  yield takeLatest(ActionTypes.FURNITURE_LOAD_REQUEST, loadingCabinetSaga);
}

export function* removeCabinetSaga() {
  /**
   * Remove the cabinet from furniture
   * Use saga instead of reducer in order to generate a historyStep
   */
  while (true) {
    const { payload } = yield take(ActionTypes.FURNITURE_REMOVE_CABINET);
    const furniture = yield select(makeSelectFurniture());
    const newFurniture: Furniture = furniture.clone();

    const { id } = payload;
    newFurniture.removeById(id);

    if (newFurniture.quantity === 1 && newFurniture.first) {
      yield put(updateCabinetSuccess(newFurniture, newFurniture.first.id));
    } else {
      yield put(updateCabinetSuccess(newFurniture, null));
    }
  }
}

export function* getThumbnailCabinet() {
  const activeTab = yield select(makeSelectActiveTab());
  let displayedTab = yield select(makeSelectDisplayedTab());

  if (activeTab !== TabNames.cabinet) {
    yield put(switchTabAction(TabNames.cabinet));

    while (displayedTab !== TabNames.cabinet) {
      displayedTab = yield select(makeSelectDisplayedTab());
      yield delay(50);
    }
  }

  return getPNGimage(100, 100);
}

export function* getImageCabinet() {
  const activeTab = yield select(makeSelectActiveTab());
  let displayedTab = yield select(makeSelectDisplayedTab());

  if (activeTab !== TabNames.cabinet) {
    yield put(switchTabAction(TabNames.cabinet));

    while (displayedTab !== TabNames.cabinet) {
      displayedTab = yield select(makeSelectDisplayedTab());
      yield delay(50);
    }
  }

  return getPNGimage();
}

export function* displayFurnitureSaga() {
  while (true) {
    yield take(ActionTypes.DISPLAY_FURNITURE_REQUEST);
    const furniture = yield select(makeSelectFurniture());
    const selectedIdCabinet = yield select(makeSelectSelectedIdCabinet());
    // const activeTab = yield select(makeSelectActiveTab());
    //
    // const furniture = yield select(makeSelectFurniture());
    // const selectedIdCabinet = yield select(makeSelectSelectedIdCabinet());
    //
    // if (activeTab !== TabNames.cabinet) {
    //   yield put(switchTabAction(TabNames.cabinet));
    //   // TODO: find a way to get the callback after ThreeJS render our cabinet
    //   // We use a delay for now, it's useless for fast computer but necessary for slow device
    //   // This solution is bad, because it's give the user, the sensation of lagging
    //   yield delay(2000);
    // }
    const newFurniture = furniture.clone();
    newFurniture.get(selectedIdCabinet).thumbnail = yield call(
      getThumbnailCabinet,
    );

    const image = yield call(getImageCabinet);
    yield put(setImageAction(image, selectedIdCabinet));
    yield put(
      updateCabinetSuccess(newFurniture, null, false, undefined, false),
    );
  }
}

export function* callBackUpdateSaga() {
  while (true) {
    const { payload } = yield take(ActionTypes.CABINET_UPDATE_SUCCESS);
    const { preview, furniture } = payload;
    if (!preview) {
      if (properties.furnitureDidUpdateCallback) {
        const furnitureData: FurnitureCallbackInfos = {
          name: furniture.getName(i18n.language),
          description: furniture.getDescription(i18n.language),
          price: furniture.displayPriceTaxInclude(
            properties.vat,
            properties.resellerRate,
            properties.decimalSeparator,
            properties.thousenSeparator,
          ),
          quantity: furniture.quantity,
        };
        properties.furnitureDidUpdateCallback(furnitureData);
      }
    }
  }
}
